"use client";

// The hero's moving picture: the real booking chart, a guest dragged to another room and a stay
// stretched. The still underneath is what the page's speed is judged on, so the clip never competes
// with it. It is fetched only after the page has loaded, on the first sign of a person or a few
// seconds later, and only where it is worth its bytes: a screen wide enough to read it, motion not
// turned off, data saver off. Its first frame is the still, so it fades in without a jump.
import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";

const WAIT_MS = 3500;
const NUDGES = ["pointermove", "scroll", "keydown", "touchstart"] as const;

export function HeroVideo({ src }: { src: string }) {
  const [wanted, setWanted] = useState(false);
  const [shown, setShown] = useState(false);
  const [paused, setPaused] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  // Paused by the visitor: scrolling back to it must not start it again.
  const held = useRef(false);

  useEffect(() => {
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (saveData || !window.matchMedia("(min-width: 768px) and (prefers-reduced-motion: no-preference)").matches) return;
    let timer: number | undefined;
    function stop() {
      window.clearTimeout(timer);
      window.removeEventListener("load", arm);
      for (const e of NUDGES) window.removeEventListener(e, go);
    }
    function go() {
      stop();
      setWanted(true);
    }
    function arm() {
      timer = window.setTimeout(go, WAIT_MS);
      for (const e of NUDGES) window.addEventListener(e, go, { passive: true });
    }
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    return stop;
  }, []);

  // Plays only while on screen.
  useEffect(() => {
    const v = video.current;
    if (!wanted || !v) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !held.current) v.play().catch(() => {});
      else v.pause();
    });
    io.observe(v);
    return () => io.disconnect();
  }, [wanted]);

  const toggle = () => {
    const v = video.current;
    if (!v) return;
    held.current = !v.paused;
    setPaused(held.current);
    if (held.current) v.pause();
    else v.play().catch(() => {});
  };

  if (!wanted) return null;
  return (
    <>
      <video
        ref={video}
        src={src}
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        disableRemotePlayback
        aria-hidden
        tabIndex={-1}
        onPlaying={() => setShown(true)}
        className={cn("absolute inset-0 size-full object-cover transition-opacity duration-700", shown ? "opacity-100" : "opacity-0")}
        data-hero-video
      />
      {shown && (
        <button
          type="button"
          onClick={toggle}
          aria-label={paused ? "Play the demo" : "Pause the demo"}
          className="absolute right-3 bottom-3 grid size-9 place-items-center rounded-full bg-black/60 text-white shadow-md ring-1 ring-white/25 transition-colors animate-in fade-in-0 duration-500 hover:bg-black/75 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
        >
          {paused ? <Play className="size-4" aria-hidden /> : <Pause className="size-4" aria-hidden />}
        </button>
      )}
    </>
  );
}
